/**
 * reportModel — pure normalization for the engine's GET /report payload.
 * No fetching, no React: PerformanceSection renders ONLY what comes out of
 * normalizeReport(), and every field it can't find comes back null so the UI
 * renders "—" instead of a fabricated number.
 *
 * ACTUAL engine contract (verified against the deployed service):
 *   GET /report → {
 *     positions: [{ pairKey, long, leverage, collateralUsd, sizeUsd,
 *                   entryPriceUsd, currentPriceUsd, unrealizedPnlUsd }],
 *                   // currentPriceUsd & unrealizedPnlUsd may be null (unpriced)
 *     ledger: { depositsUsd, withdrawalsUsd, tradingInUsd, tradingOutUsd,
 *               externalFlows: [{ timestampMs, amountUsd }],
 *               externalCounterparties: { <address>: number },
 *               openingEquityUsd, openingTimestampMs },
 *     realizedPnlUsd,
 *     totalPnlUsd,
 *     roi: { simplePct, moneyWeightedAnnualizedPct, timeWeightedPct }, // any may be null
 *     snapshot: { timestampMs, equityUsd, netExternalFlowSinceLastUsd },
 *     ledgerComplete,
 *     recentEvents: [{ userId, wallet, atMs, at, pair, intent, status,
 *                      detail?, txHash?, priceUsd?, strategy? }],
 *     monthlyReturns: { months: [{ month:"2026-07", returnPct, partial,
 *                                  periods }],   // oldest-first, gaps ABSENT
 *                       averageMonthlyReturnPct, // mean of COMPLETED months;
 *                                                // null if none
 *                       completedMonths,
 *                       insufficientHistory }    // true until ≥1 completed month
 *   }
 *   GET /report/history → { wallet,
 *                           points: [{ timestampMs, equityUsd,
 *                                      netExternalFlowSinceLastUsd }], // oldest-first
 *                           monthlyReturns } // same shape as on /report
 *   Errors: 401 bad token; 404 { error: "not enrolled" } (callers treat as
 *           "no report yet"); 500 { error, detail }.
 *
 * Derived display values (the payload does not carry these directly):
 *  - equityUsd        = snapshot.equityUsd
 *  - unrealizedPnlUsd = totalPnlUsd − realizedPnlUsd (null if either is missing)
 *  - netDepositsUsd   = ledger.depositsUsd − ledger.withdrawalsUsd
 *                       (null if either is missing — never a partial figure)
 *
 * COMPLIANCE INVARIANTS:
 *  - "Deposited" is net funding. Gross deposits (ledger.depositsUsd) appear
 *    only in a tooltip, labeled as gross; never substituted for the net figure.
 *  - roi.timeWeightedPct is null until the engine has ≥2 snapshots ⇒
 *    "—" / "building history", never a number.
 *  - The engine signals unpriced positions with null currentPriceUsd /
 *    unrealizedPnlUsd — those render "— (unpriced)", never a fabricated mark.
 *  - The growth chart plots ONLY /report/history points (< 2 plottable points
 *    ⇒ "building history" empty state); points missing a timestamp or equity
 *    are dropped, never interpolated.
 *  - "Average monthly return" shows a number ONLY when the engine says
 *    insufficientHistory is false; while true it renders "building history".
 *    Gap months are absent from months[] and stay absent — never fabricated.
 */

/* Coerce to a finite number or null — null is what the formatters turn into "—". */
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const str = (v) => (v === null || v === undefined || v === "" ? null : String(v));

/**
 * Normalize the monthlyReturns block (present on BOTH /report and
 * /report/history). Null in ⇒ null out, so a payload from an engine that
 * doesn't compute monthly returns yet renders "—", not zeros.
 */
export function normalizeMonthlyReturns(raw) {
  if (!raw || typeof raw !== "object") return null;
  const months = (Array.isArray(raw.months) ? raw.months : [])
    .map((m) => ({
      month: str(m?.month),
      returnPct: num(m?.returnPct),
      partial: m?.partial === true,
      periods: num(m?.periods),
    }))
    // A month entry without its "YYYY-MM" key can't be labeled honestly.
    .filter((m) => m.month !== null);
  return {
    months,
    averageMonthlyReturnPct: num(raw.averageMonthlyReturnPct),
    completedMonths: num(raw.completedMonths),
    // Strictly the engine's flag: the tile shows a number ONLY when this is
    // exactly false. Missing/junk ⇒ null ⇒ the tile falls back to "—".
    insufficientHistory:
      typeof raw.insufficientHistory === "boolean" ? raw.insufficientHistory : null,
  };
}

/**
 * Normalize GET /report/history. Points that can't be plotted (no timestamp
 * or no equity value) are dropped — the chart never interpolates or invents
 * a coordinate.
 */
export function normalizeReportHistory(raw) {
  if (!raw || typeof raw !== "object") return null;
  const points = (Array.isArray(raw.points) ? raw.points : [])
    .map((p) => ({
      timestampMs: num(p?.timestampMs),
      equityUsd: num(p?.equityUsd),
      netExternalFlowSinceLastUsd: num(p?.netExternalFlowSinceLastUsd),
    }))
    .filter((p) => p.timestampMs !== null && p.equityUsd !== null);
  return {
    wallet: str(raw.wallet),
    points,
    monthlyReturns: normalizeMonthlyReturns(raw.monthlyReturns),
  };
}

export function normalizeReport(raw) {
  if (!raw || typeof raw !== "object") return null;

  const ledger = raw.ledger && typeof raw.ledger === "object" ? raw.ledger : {};
  const roi = raw.roi && typeof raw.roi === "object" ? raw.roi : {};
  const snapshot = raw.snapshot && typeof raw.snapshot === "object" ? raw.snapshot : {};

  const grossDepositsUsd = num(ledger.depositsUsd);
  const withdrawalsUsd = num(ledger.withdrawalsUsd);
  // NET funding needs both sides of the ledger; a partial figure would lie.
  const netDepositsUsd =
    grossDepositsUsd !== null && withdrawalsUsd !== null
      ? grossDepositsUsd - withdrawalsUsd
      : null;

  const realizedPnlUsd = num(raw.realizedPnlUsd);
  const totalPnlUsd = num(raw.totalPnlUsd);
  const unrealizedPnlUsd =
    totalPnlUsd !== null && realizedPnlUsd !== null ? totalPnlUsd - realizedPnlUsd : null;

  const positions = (Array.isArray(raw.positions) ? raw.positions : []).map((p) => {
    const currentPriceUsd = num(p?.currentPriceUsd);
    const posUnrealizedUsd = num(p?.unrealizedPnlUsd);
    return {
      pairKey: str(p?.pairKey) || "—",
      long: p?.long === undefined || p?.long === null ? null : Boolean(p.long),
      leverage: num(p?.leverage),
      collateralUsd: num(p?.collateralUsd),
      sizeUsd: num(p?.sizeUsd),
      entryPriceUsd: num(p?.entryPriceUsd),
      currentPriceUsd,
      unrealizedPnlUsd: posUnrealizedUsd,
      // The engine nulls the mark for pairs it can't price.
      priced: currentPriceUsd !== null && posUnrealizedUsd !== null,
    };
  });

  const recentEvents = (Array.isArray(raw.recentEvents) ? raw.recentEvents : []).map((e) => ({
    atMs: num(e?.atMs),
    at: str(e?.at),
    pair: str(e?.pair),
    intent: str(e?.intent),
    status: str(e?.status),
    detail: str(e?.detail),
    txHash: str(e?.txHash),
    priceUsd: num(e?.priceUsd),
    strategy: str(e?.strategy),
  }));

  return {
    equityUsd: num(snapshot.equityUsd),
    snapshotTimestampMs: num(snapshot.timestampMs),
    realizedPnlUsd,
    unrealizedPnlUsd,
    totalPnlUsd,
    netDepositsUsd,
    grossDepositsUsd, // tooltip only — NEVER shown as "Deposited"
    withdrawalsUsd,
    simpleRoiPct: num(roi.simplePct),
    moneyWeightedRoiPct: num(roi.moneyWeightedAnnualizedPct),
    timeWeightedRoiPct: num(roi.timeWeightedPct), // null until ≥2 snapshots
    ledgerComplete: typeof raw.ledgerComplete === "boolean" ? raw.ledgerComplete : null,
    monthlyReturns: normalizeMonthlyReturns(raw.monthlyReturns),
    positions,
    unpricedCount: positions.filter((p) => !p.priced).length,
    recentEvents,
  };
}
